/**
 * WordPress dependencies
 */
import type {
	select as _select,
	dispatch as _dispatch,
} from '@safe-wordpress/data';

/**
 * External dependencies
 */
import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import type {
	AlternativeIndex,
	BoundingBox,
	Click,
	ClickVisibilityProps,
	ClicksPerSquare,
	ConfettiOption,
	ConfettiType,
	Dict,
	HeatmapDataStatus,
	HeatmapLabels,
	HeatmapResolution,
	ClickWithVisibility,
	Scroll,
	ScrollSummary,
} from '@nab/types';

/**
 * Internal dependencies
 */
import { getDocumentHeight } from './utils';
import { getElementByXPath } from '../../../../public/tracking/xpath';
import { getDocumentHeight as getBodyHeight } from '../../../../public/utils/helpers';

// ===================
// CONSTANTS AND UTILS
// ===================

import type { store as dataStore } from '@nab/data';
const NAB_DATA = 'nab/data' as unknown as typeof dataStore;

import type { store as heatmapStore } from '../../../pages/heatmap-results/store';
const NAB_HEATMAP = 'nab/heatmap' as unknown as typeof heatmapStore;

const select = (
	( ( window.parent as unknown as Dict )?.wp as Dict )?.data as Dict
 )?.select as typeof _select;
const dispatch = (
	( ( window.parent as unknown as Dict )?.wp as Dict )?.data as Dict
 )?.dispatch as typeof _dispatch;

type XYCoords = [ number, number ];
type ExpandedClick = Click & {
	htmlElement: HTMLElement | null;
};

// =======
// EXPORTS
// =======

export function areRawResults(
	mode: HeatmapDataStatus[ 'mode' ] | HeatmapDataStatus[ 'mode' ][]
): boolean {
	const alternative = getAlternative();
	const status = select( NAB_HEATMAP ).getRawResultStatus( alternative ).mode;
	const modes = 'string' === typeof mode ? [ mode ] : mode;
	return modes.some( ( m ) => m === status );
}

export function getRawClicks(): ReadonlyArray< Click > {
	return select( NAB_HEATMAP ).getRawClicks( getAlternative() );
}

export function getRawScrolls(): ReadonlyArray< Scroll > {
	return select( NAB_HEATMAP ).getRawScrolls( getAlternative() );
}

export function getHeatmapClicks(): ReadonlyArray< ClickWithVisibility > {
	return select( NAB_HEATMAP ).getClicksInResolution(
		getAlternative(),
		getActiveResolution()
	);
}

export function getClicksPerSquare(): ClicksPerSquare {
	return select( NAB_HEATMAP ).getClicksPerSquare(
		getAlternative(),
		getActiveResolution()
	);
}

export function getScrollSummary(): ScrollSummary {
	return select( NAB_HEATMAP ).getScrollSummary(
		getAlternative(),
		getActiveResolution()
	);
}

export function getConfettiClicks(): ReadonlyArray< ClickWithVisibility > {
	return getHeatmapClicks().filter( isClickVisibleInConfetti );
}

export function getConfettiFilterOptions(): Partial<
	Record< ConfettiType, Dict< ConfettiOption > >
> {
	return select( NAB_HEATMAP ).getConfettiFilterOptions(
		getAlternative(),
		getActiveResolution()
	);
}

export function getActiveConfettiFilter(): ConfettiType {
	return select( NAB_HEATMAP ).getActiveFilter();
}

export function getDisabledConfettiFilterOptions(): ReadonlyArray< string > {
	return select( NAB_HEATMAP ).getDisabledFilterOptions();
}

export function getIntensity(): number {
	return select( NAB_HEATMAP ).getIntensity();
}

export function getOpacity(): number {
	return select( NAB_HEATMAP ).getOpacity();
}

export function isInteractive(): boolean {
	return select( NAB_HEATMAP ).isInteractive();
}

export function markScriptAsReady(): void {
	const alt = getAlternative();
	void dispatch( NAB_HEATMAP ).setIFrameStatus( alt, 'script-ready' );
}

export function markPageAsReady(): void {
	const alt = getAlternative();
	void dispatch( NAB_HEATMAP ).setIFrameStatus( alt, 'page-ready' );
}

export function processResults(): void {
	const alt = getAlternative();
	const res = getActiveResolution();
	const clicks = getRawClicks().map( expandClick );
	const page = {
		clickVisibilityProps: computeClickVisibilityProps( clicks ),
		dimensions: getPageDimensions(),
	};
	void dispatch( NAB_HEATMAP ).setPageInfo( alt, res, page );
	void dispatch( NAB_HEATMAP ).processResults( alt, res );
}

export function onWindowResize(): void {
	const alt = getAlternative();
	const res = getActiveResolution();
	const pageDimensions = getPageDimensions();
	const storeDimensions = select( NAB_HEATMAP ).getPageDimensions( alt, res );
	if ( isEqual( pageDimensions, storeDimensions ) ) {
		return;
	}
	processResults();
}

export const getIFrameLabels = (): HeatmapLabels =>
	select( NAB_HEATMAP ).getIFrameLabels();

// =======
// HELPERS
// =======

function expandClick( click: Click ): ExpandedClick {
	const selector = click.xpath || click.cssPath;
	if ( ! selector ) {
		return { ...click, htmlElement: null };
	}

	const el = getAllSelectors( selector ).reduce(
		( r, s ) => {
			if ( r ) {
				return r;
			}
			try {
				return click.xpath
					? getElementByXPath( s )
					: document.querySelector( s );
			} catch ( _ ) {
				return r;
			}
		},
		null as Node | null
	);

	return { ...click, htmlElement: isHtmlElement( el ) ? el : null };
}

const getAlternative = (): AlternativeIndex =>
	Number.parseInt(
		window.location.search
			.replace( /^\?/, '' )
			.split( '&' )
			.find( ( x ) => x.startsWith( 'alternative=' ) )
			?.split( '=' )[ 1 ] ?? '0'
	) || 0;

function getActiveResolution(): HeatmapResolution {
	return select( NAB_HEATMAP ).getActiveResolution();
}

const getPageDimensions = () => ( {
	width: window.innerWidth,
	bodyHeight: getBodyHeight(),
	iframeHeight: getDocumentHeight(),
} );

function computeClickVisibilityProps(
	clicks: ReadonlyArray< ExpandedClick >
): Record< string, ClickVisibilityProps > {
	const boundingBoxes = computeBoundingBoxes( clicks );
	const interactive = isInteractive();

	return mapValues( keyBy( clicks, 'id' ), ( c ): ClickVisibilityProps => {
		const coords = getClickCoordinates( boundingBoxes, c );
		if ( ! coords ) {
			return { status: 'missing' };
		}

		if ( ! interactive || areGlobalCoordsOutsideViewport( coords ) ) {
			return {
				coords: { x: coords[ 0 ], y: coords[ 1 ] },
				status: 'visible',
			};
		}

		const viewportCoords = transformGlobalToViewportCoords( coords );
		const match = document.elementFromPoint( ...viewportCoords );
		const element = isHtmlElement( match ) ? match : null;
		return {
			coords: { x: coords[ 0 ], y: coords[ 1 ] },
			status: element === c.htmlElement ? 'visible' : 'invisible',
		};
	} );
}

const computeBoundingBoxes = (
	clicks: ReadonlyArray< ExpandedClick >
): Record< string, BoundingBox > =>
	pickBy(
		mapValues(
			keyBy(
				clicks.map( ( c ) => ( {
					...c,
					_selector: c.xpath || c.cssPath,
				} ) ),
				'_selector'
			),
			( click ): BoundingBox | undefined => {
				if ( ! click.htmlElement ) {
					return undefined;
				}

				const bodyBox = document.body.getBoundingClientRect();
				const elBox = click.htmlElement.getBoundingClientRect();
				return {
					top: elBox.top - bodyBox.top,
					left: elBox.left - bodyBox.left,
					width: elBox.width,
					height: elBox.height,
				};
			}
		),
		< T >( x: T | undefined ): x is T => undefined !== x
	);

function getClickCoordinates(
	boundingBoxes: Record< string, BoundingBox >,
	click: Click
): XYCoords | false {
	const box = boundingBoxes[ click.xpath || click.cssPath ];
	if ( ! box ) {
		return false;
	}
	const x = box.left + click.x * box.width;
	const y = box.top + click.y * box.height;
	return [ x, y ];
}

function areGlobalCoordsOutsideViewport( coords: XYCoords ): boolean {
	const [ viewportX, viewportY ] = transformGlobalToViewportCoords( coords );

	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;

	return (
		viewportX < 0 ||
		viewportX > viewportWidth ||
		viewportY < 0 ||
		viewportY > viewportHeight
	);
}

const transformGlobalToViewportCoords = ( [ x, y ]: XYCoords ): XYCoords => [
	x - window.scrollX,
	y - window.scrollY,
];

function getAllSelectors( selector: string ): ReadonlyArray< string > {
	const { getExperiment, getPageAttribute } = select( NAB_DATA );
	const experimentId = getPageAttribute( 'editor/activeExperiment' );
	const experiment = getExperiment( experimentId );
	if ( ! experiment ) {
		return [ selector ];
	}

	if ( experiment.type === 'nab/heatmap' ) {
		return [ selector ];
	}

	const { getActiveAlternative } = select( NAB_HEATMAP );
	const altIdx = getActiveAlternative();
	if ( altIdx === 0 ) {
		return [ selector ];
	}

	const control = experiment.alternatives[ 0 ];
	const alternative =
		undefined !== altIdx ? experiment.alternatives[ altIdx ] : undefined;

	const controlPost = control?.attributes.postId;
	const alternativePost = alternative?.attributes.postId;
	if ( ! isPostId( controlPost ) || ! isPostId( alternativePost ) ) {
		return [ selector ];
	}

	return [
		selector,
		selector.replace(
			new RegExp( `([\\b_])?${ controlPost }([\\b_])?`, 'g' ),
			`$1${ alternativePost }$2`
		),
		selector.replace(
			new RegExp( `([\\b_])?${ alternativePost }([\\b_])?`, 'g' ),
			`$1${ controlPost }$2`
		),
	];
}

const isPostId = ( id: unknown ): id is number =>
	'number' === typeof id && 0 < id;

function isHtmlElement( n: Node | null ): n is HTMLElement {
	return !! n && Node.ELEMENT_NODE === n.nodeType;
}

function isClickVisibleInConfetti( click: Click ): boolean {
	const actualFilterOptions = getConfettiFilterOptions();
	const activeFilter = getActiveConfettiFilter();
	const disabledFilterOptions = getDisabledConfettiFilterOptions();

	let filterValue = click[ activeFilter ];
	if (
		! Object.keys( actualFilterOptions[ activeFilter ] || {} ).includes(
			`${ filterValue }`
		)
	) {
		filterValue = 'other';
	}

	return ! disabledFilterOptions.includes( `${ filterValue }` );
}
